import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  Button,
  Dropdown,
  message,
  Alert,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  ShoppingCartOutlined,
  ClockCircleOutlined,
  CarOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  DownOutlined,
  EllipsisOutlined,
  ShopOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import { useNavigate, Link } from 'react-router-dom';
import {
  getPresetRange,
  toQueryRange,
  DEFAULT_PRESET,
} from '../utils/dateRanges';
import DateRangeFilter, {
  DateRangeFilterValue,
} from './dashboard/DateRangeFilter';
import { formatVnDateTimeEn } from '../utils/vnDateTime';
import { MerchantAvatar } from './MerchantAvatar';
import {
  adminOrderStatusLabel,
  adminOrderStatusVariant,
  PENDING_STATUSES,
  PREPARING_STATUSES,
} from '../utils/orderStatus';

interface Order {
  id: number;
  order_number: string;
  total: number;
  status: string;
  created_at: string;
  customerName?: string;
  customer_phone?: string;
  restaurantName?: string;
  restaurant_image?: string;
}

interface OrdersResponse {
  success: boolean;
  data: Order[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const PAGE_SIZE = 5;
const SUMMARY_LIMIT = 10000;

function formatCurrency(value: number | undefined | null): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function orderIdDisplay(order: Order): string {
  return order.order_number ? `#${order.order_number}` : '–';
}

function getInitials(name: string | undefined | null): string {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#ea580c', '#db2777', '#4b5563'];

function avatarColor(name: string | undefined | null): string {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function statusBadgeVariant(status: string) {
  return adminOrderStatusVariant(status);
}

const getInitialDateRange = (): DateRangeFilterValue => {
  const range = getPresetRange(DEFAULT_PRESET);
  const query = toQueryRange(range.from, range.to);
  return {
    startDate: query.startDate,
    endDate: query.endDate,
    presetId: DEFAULT_PRESET,
  };
};

function statusLabel(status: string): string {
  return adminOrderStatusLabel(status);
}

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [dateFilterValue, setDateFilterValue] =
    useState<DateRangeFilterValue>(getInitialDateRange);
  const [merchantFilter, setMerchantFilter] = useState<number | undefined>(
    undefined
  );
  const [merchants, setMerchants] = useState<
    { id: number; name: string; restaurantId?: number }[]
  >([]);
  const [summaryCounts, setSummaryCounts] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    delivered: 0,
  });

  useEffect(() => {
    const loadMerchants = async () => {
      try {
        const res = await api.get<{
          success: boolean;
          data: {
            id: number;
            name: string;
            restaurantId?: number;
            restaurant_id?: number;
            restaurantName?: string;
          }[];
        }>('/merchants', { params: { page: 1, limit: 500 } });
        if (res.data.success && Array.isArray(res.data.data)) {
          setMerchants(
            res.data.data.map(m => ({
              id: m.id,
              name: m.restaurantName || m.name || `Merchant #${m.id}`,
              restaurantId: m.restaurantId ?? m.restaurant_id,
            }))
          );
        }
      } catch {
        // Merchant filter optional
      }
    };
    loadMerchants();
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page,
        limit: PAGE_SIZE,
      };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      if (dateFilterValue.startDate) params.from = dateFilterValue.startDate;
      if (dateFilterValue.endDate) params.to = dateFilterValue.endDate;
      if (merchantFilter != null) params.restaurant_id = merchantFilter;
      const res = await api.get<OrdersResponse>('/orders', { params });
      const body = res.data;
      if (body.success && Array.isArray(body.data)) {
        setOrders(body.data);
        const pag = body.pagination;
        if (pag && Number.isFinite(pag.total)) {
          setTotal(pag.total);
        }
      }
      setError(null);
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { message?: string }; status?: number };
        message?: string;
      };
      setError(
        ax.response?.data?.message || ax.message || 'Failed to load orders'
      );
      message.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [
    page,
    statusFilter,
    search,
    dateFilterValue.startDate,
    dateFilterValue.endDate,
    merchantFilter,
  ]);

  const fetchSummary = useCallback(async () => {
    try {
      const params: Record<string, string | number> = {
        page: 1,
        limit: SUMMARY_LIMIT,
      };
      if (dateFilterValue.startDate) params.from = dateFilterValue.startDate;
      if (dateFilterValue.endDate) params.to = dateFilterValue.endDate;
      const res = await api.get<OrdersResponse>('/orders', {
        params,
      });
      const body = res.data;
      if (!body.success || !Array.isArray(body.data)) return;
      const data = body.data;
      const pag = body.pagination;
      const totalCount =
        pag && Number.isFinite(pag.total) ? pag.total : data.length;
      const pending = data.filter(o =>
        PENDING_STATUSES.includes(
          (o.status || '').toLowerCase() as (typeof PENDING_STATUSES)[number]
        )
      ).length;
      const inProgress = data.filter(o =>
        PREPARING_STATUSES.includes(
          (o.status || '').toLowerCase() as (typeof PREPARING_STATUSES)[number]
        )
      ).length;
      const delivered = data.filter(
        o => (o.status || '').toLowerCase() === 'delivered'
      ).length;
      setSummaryCounts({
        total: totalCount,
        pending,
        inProgress,
        delivered,
      });
    } catch {
      // Keep previous summary
    }
  }, [dateFilterValue.startDate, dateFilterValue.endDate]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);

  const columns = [
    {
      title: 'ORDER ID',
      key: 'order_id',
      dataIndex: 'id',
      render: (_: unknown, record: Order) => (
        <button
          type="button"
          className="admin-orders-link-id"
          onClick={() => navigate(`/orders/${record.id}`)}
        >
          {orderIdDisplay(record)}
        </button>
      ),
    },
    {
      title: 'CUSTOMER',
      key: 'customer',
      render: (_: unknown, record: Order) => (
        <div className="admin-orders-customer">
          <div
            className="admin-orders-avatar"
            style={{ backgroundColor: avatarColor(record.customerName) }}
          >
            {getInitials(record.customerName)}
          </div>
          <div className="admin-orders-customer-text">
            <div className="admin-orders-customer-name">
              {record.customerName || '–'}
            </div>
            <div className="admin-orders-customer-email">
              {record.customer_phone || '–'}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'MERCHANT',
      key: 'merchant',
      render: (_: unknown, record: Order) => (
        <div className="admin-orders-merchant">
          <MerchantAvatar
            name={record.restaurantName}
            imageUrl={record.restaurant_image}
            size={28}
            borderRadius={6}
          />
          <span>{record.restaurantName || '–'}</span>
        </div>
      ),
    },
    {
      title: 'DATE',
      dataIndex: 'created_at',
      key: 'date',
      render: (v: string) => formatVnDateTimeEn(v),
    },
    {
      title: 'TOTAL AMOUNT',
      dataIndex: 'total',
      key: 'total_amount',
      render: (v: number) => (
        <span className="admin-orders-total">{formatCurrency(v)}</span>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const variant = statusBadgeVariant(status);
        const label = statusLabel(status);
        return (
          <span
            className={`admin-orders-status-badge admin-orders-status-${variant}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 64,
      render: (_: unknown, record: Order) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: 'View',
                onClick: () => navigate(`/orders/${record.id}`),
              },
            ] as MenuProps['items'],
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <button
            type="button"
            className="admin-orders-actions-btn"
            aria-label="Actions"
            onClick={e => e.stopPropagation()}
          >
            <EllipsisOutlined />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="admin-orders-page">
      <header className="admin-orders-header">
        <h1 className="admin-orders-title">Order Management</h1>
        <nav className="admin-orders-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="admin-orders-breadcrumb-sep">&gt;</span>
          <span className="admin-orders-breadcrumb-current">
            Order Management
          </span>
        </nav>
      </header>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          className="admin-orders-alert"
        />
      )}

      <section className="admin-orders-cards">
        <Card className="admin-orders-card">
          <div className="admin-orders-card-body">
            <div className="admin-orders-card-content">
              <div className="admin-orders-card-title">Total Orders</div>
              <div className="admin-orders-card-value">
                {summaryCounts.total.toLocaleString('en-US')}
              </div>
            </div>
            <div className="admin-orders-card-icon admin-orders-card-icon-blue">
              <ShoppingCartOutlined />
            </div>
          </div>
        </Card>
        <Card className="admin-orders-card">
          <div className="admin-orders-card-body">
            <div className="admin-orders-card-content">
              <div className="admin-orders-card-title">Pending</div>
              <div className="admin-orders-card-value">
                {summaryCounts.pending.toLocaleString('en-US')}
              </div>
            </div>
            <div className="admin-orders-card-icon admin-orders-card-icon-orange">
              <ClockCircleOutlined />
            </div>
          </div>
        </Card>
        <Card className="admin-orders-card">
          <div className="admin-orders-card-body">
            <div className="admin-orders-card-content">
              <div className="admin-orders-card-title">In Progress</div>
              <div className="admin-orders-card-value">
                {summaryCounts.inProgress.toLocaleString('en-US')}
              </div>
            </div>
            <div className="admin-orders-card-icon admin-orders-card-icon-purple">
              <CarOutlined />
            </div>
          </div>
        </Card>
        <Card className="admin-orders-card">
          <div className="admin-orders-card-body">
            <div className="admin-orders-card-content">
              <div className="admin-orders-card-title">Delivered</div>
              <div className="admin-orders-card-value">
                {summaryCounts.delivered.toLocaleString('en-US')}
              </div>
            </div>
            <div className="admin-orders-card-icon admin-orders-card-icon-green">
              <CheckCircleOutlined />
            </div>
          </div>
        </Card>
      </section>

      <section className="admin-orders-filters">
        <div className="admin-orders-filters-left">
          <Input
            placeholder="Search Order ID, Customer"
            prefix={<SearchOutlined className="admin-orders-input-prefix" />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onPressEnter={() => {
              setPage(1);
              fetchList();
            }}
            className="admin-orders-search-input"
            allowClear
          />
          <DateRangeFilter
            value={dateFilterValue}
            onChange={v => {
              setDateFilterValue(v);
              setPage(1);
            }}
            loading={loading}
          />
        </div>
        <div className="admin-orders-filters-middle">
          <Select
            showSearch
            allowClear
            placeholder="All Merchants"
            suffixIcon={<DownOutlined />}
            className="admin-orders-select"
            value={merchantFilter ?? 'all'}
            optionFilterProp="label"
            onChange={v => {
              setMerchantFilter(
                v === 'all' || v == null ? undefined : Number(v)
              );
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All Merchants' },
              ...merchants
                .filter(m => m.restaurantId != null && m.restaurantId > 0)
                .map(m => ({
                  value: m.restaurantId as number,
                  label: m.name,
                })),
            ]}
          />
          <Select
            placeholder="All Status"
            suffixIcon={<DownOutlined />}
            className="admin-orders-select"
            value={statusFilter ?? 'all'}
            onChange={v => {
              setStatusFilter(v === 'all' ? undefined : v);
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'pending', label: 'Pending' },
              { value: 'preparing', label: 'Preparing' },
              { value: 'delivered', label: 'Delivered' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
          />
        </div>
        {/* <div className="admin-orders-filters-right">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            className="admin-orders-export-btn"
          >
            Export Orders
          </Button>
        </div> */}
      </section>

      <Card className="admin-orders-table-card">
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={false}
          className="admin-orders-table"
          onRow={record => ({
            onClick: () => navigate(`/orders/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <footer className="admin-orders-pagination">
        <span className="admin-orders-pagination-text">
          Showing {start} to {end} of {total.toLocaleString('en-US')} results
        </span>
        <div className="admin-orders-pagination-btns">
          <Button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            disabled={page >= Math.ceil(total / PAGE_SIZE) || total === 0}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      </footer>
    </div>
  );
}
